import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { myOrdersQuery } from "@/lib/queries";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";
import { InvoiceDownloadButton } from "@/components/invoice-download-button";
import { useT } from "@/i18n";

export const Route = createFileRoute("/app/invoices/")({
  head: () =>
    privatePageMetadata(
      "/app/invoices",
      "Invoices | BeHot.Now",
      "Download your BeHot.Now invoices.",
    ),
  component: InvoicesPage,
});

function InvoicesPage() {
  const { user } = useAuth();
  const { t } = useT();
  const { data: orders = [], isLoading } = useQuery(myOrdersQuery(user?.id));
  const paid = orders.filter((o) => o.status === "paid");

  return (
    <div className="space-y-3">
      {isLoading && (
        <div className="card-surface p-6 text-sm text-muted-foreground">{t("dashboard.invoices.loading")}</div>
      )}
      {!isLoading && paid.length === 0 && (
        <div className="card-surface p-6 text-sm text-muted-foreground">
          {t("dashboard.invoices.noDocuments")}
        </div>
      )}
      {paid.map((o) => (
        <div key={o.id} className="card-surface flex flex-wrap items-center gap-3 p-4">
          <FileText className="size-5 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{o.invoice_number ?? o.receipt_number ?? o.id.slice(0, 8)}</p>
            <p className="text-xs text-muted-foreground">
              {o.paid_at ? new Date(o.paid_at).toLocaleDateString() : t("dashboard.invoices.na")} ·{" "}
              {o.listing?.name ?? t("dashboard.invoices.boostFallback")}
            </p>
          </div>
          <span className="font-display font-bold">
            {formatMoney(o.amount_paid_cents, o.currency)}
          </span>
          <div className="flex gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/app/invoices/$id" params={{ id: o.id }}>
                {t("dashboard.invoices.view")}
              </Link>
            </Button>
            <InvoiceDownloadButton orderId={o.id} label={t("dashboard.invoices.pdf")} />
          </div>
        </div>
      ))}
    </div>
  );
}
